import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronRight, ChevronLeft } from "lucide-react";
import { api } from "@/lib/api";
import ProductImage from "@/components/ProductImage";
import { HERO_IMAGE_SIZES, prefetchProductImages, productImageSrc } from "@/lib/images";
import { normalizeProductList } from "@/lib/apiHelpers";
import { cacheGet, cacheKey, fetchCached } from "@/lib/dataCache";
import ProductCard from "@/components/ProductCard";
import RecentlyViewed from "@/components/RecentlyViewed";
import HeroVisual from "@/components/HeroVisual";
import { freeShippingLabel } from "@/lib/shipping";
import { HERO_TRANSITION, useHeroAutoplay } from "@/lib/heroMotion";

const fade = {
  hidden: { opacity: 0, y: 12 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.35, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] } }),
};

import { HERO_SLIDE_CONFIG, resolveHeroSlides } from "@/lib/heroSlides";
import { EDITORIAL_FALLBACK_IMAGE, FOUNDER_IMAGE } from "@/lib/brandAssets";
const pickUniqueByImage = (products, count, usedImages) => {
  const picked = [];
  for (const product of products) {
    if (picked.length >= count) break;
    if (!product?.image || usedImages.has(product.image)) continue;
    picked.push(product);
    usedImages.add(product.image);
  }
  return picked;
};

const mergeUniqueProducts = (...lists) => {
  const byId = new Map();
  for (const list of lists) {
    for (const product of list || []) {
      if (product?.id && !byId.has(product.id)) byId.set(product.id, product);
    }
  }
  return [...byId.values()];
};

function WhyOoriImage({ founderSrc, productSrc, fallbackSrc, alt }) {
  const [useFounder, setUseFounder] = useState(true);

  useEffect(() => {
    setUseFounder(true);
  }, [founderSrc, productSrc]);

  if (useFounder) {
    return (
      <img
        src={founderSrc}
        alt="Payal, founder of OORI Label"
        loading="eager"
        decoding="async"
        fetchPriority="high"
        className="absolute inset-0 h-full w-full object-cover object-[50%_22%]"
        data-testid="why-oori-image"
        onError={() => setUseFounder(false)}
      />
    );
  }

  return (
    <ProductImage
      src={productSrc || fallbackSrc}
      alt={alt}
      priority
      sizes="(max-width: 1024px) 100vw, 45vw"
      className="absolute inset-0 h-full w-full object-cover object-[50%_22%]"
    />
  );
}

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [heroProducts, setHeroProducts] = useState({});
  const [slide, setSlide] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);

  const productPool = useMemo(
    () => mergeUniqueProducts(featured, catalog),
    [featured, catalog]
  );

  const heroSlides = useMemo(
    () => resolveHeroSlides(productPool, heroProducts),
    [productPool, heroProducts]
  );

  useEffect(() => {
    heroSlides.forEach((s) => {
      if (!s?.image) return;
      const img = new Image();
      img.src = productImageSrc(s.image, "md");
    });
  }, [heroSlides]);

  const advanceSlide = useCallback(() => {
    setSlide((s) => (s + 1) % heroSlides.length);
  }, [heroSlides.length]);

  const { restart: restartAutoplay } = useHeroAutoplay({
    count: heroSlides.length,
    paused: heroPaused,
    onAdvance: advanceSlide,
  });

  const goToSlide = useCallback(
    (index) => {
      setSlide(index);
      restartAutoplay();
    },
    [restartAutoplay]
  );

  const next = useCallback(() => {
    setSlide((s) => (s + 1) % heroSlides.length);
    restartAutoplay();
  }, [heroSlides.length, restartAutoplay]);

  const prev = useCallback(() => {
    setSlide((s) => (s - 1 + heroSlides.length) % heroSlides.length);
    restartAutoplay();
  }, [heroSlides.length, restartAutoplay]);

  useEffect(() => {
    let active = true;
    const catKey = cacheKey(["categories"]);
    const featKey = cacheKey(["featured"]);
    const catalogKey = cacheKey(["catalog", "home"]);
    const cachedCats = cacheGet(catKey);
    const cachedFeat = cacheGet(featKey);
    const cachedCatalog = cacheGet(catalogKey);
    if (cachedCats) setCategories(cachedCats);
    if (cachedFeat) setFeatured(cachedFeat);
    if (cachedCatalog) setCatalog(cachedCatalog);

    const applyCats = (data) => { if (active) setCategories(data); };
    const applyFeat = (data) => { if (active) setFeatured(data); };
    const applyCatalog = (data) => { if (active) setCatalog(data); };

    Promise.all([
      fetchCached(catKey, () => api.get("/categories").then((r) => (Array.isArray(r.data) ? r.data : [])), { onUpdate: applyCats }),
      fetchCached(featKey, () =>
        api.get("/products/featured").then((r) => normalizeProductList(r.data)),
        { onUpdate: applyFeat }
      ),
      fetchCached(catalogKey, () =>
        api.get("/products", { params: { limit: 48 } }).then((r) => normalizeProductList(r.data)),
        { onUpdate: applyCatalog }
      ),
    ])
      .then(([cats, feat, catalogItems]) => {
        applyCats(cats);
        applyFeat(feat);
        applyCatalog(catalogItems);
      })
      .catch(() => {
        if (!active) return;
        if (!cachedCats) setCategories([]);
        if (!cachedFeat) setFeatured([]);
        if (!cachedCatalog) setCatalog([]);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const pool = mergeUniqueProducts(featured, catalog);
    const missingIds = HERO_SLIDE_CONFIG
      .map((s) => s.productId)
      .filter((id) => id && !pool.some((p) => p.id === id));

    if (!missingIds.length) return;

    let active = true;
    Promise.all(
      missingIds.map((id) =>
        api.get(`/products/${id}`).then((r) => r.data?.product || r.data).catch(() => null)
      )
    ).then((products) => {
      if (!active) return;
      setHeroProducts((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const p of products) {
          if (p?.id && !next[p.id]) {
            next[p.id] = p;
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    });

    return () => {
      active = false;
    };
  }, [featured, catalog]);

  const current = heroSlides[slide] || heroSlides[0];

  /** From identity anchor downward: no image URL appears twice. */
  const { featuredGrid, editorialProduct, lookbookTiles } = useMemo(() => {
    const usedImages = new Set(
      categories.map((c) => c.cover_image).filter(Boolean)
    );
    const pool = mergeUniqueProducts(featured, catalog);

    const grid = pickUniqueByImage(pool, 4, usedImages);
    const editorial = pickUniqueByImage(pool, 1, usedImages)[0] || null;
    const lookbook = pickUniqueByImage(pool, 5, usedImages);

    return { featuredGrid: grid, editorialProduct: editorial, lookbookTiles: lookbook };
  }, [categories, featured, catalog]);

  useEffect(() => {
    prefetchProductImages(featuredGrid, { limit: 4 });
    if (editorialProduct?.image) prefetchProductImages([editorialProduct], { limit: 1, variant: "md" });
    prefetchProductImages([{ image: EDITORIAL_FALLBACK_IMAGE }], { limit: 1, variant: "md" });
    prefetchProductImages(lookbookTiles, { limit: 5 });
    prefetchProductImages(categories.map((c) => ({ image: c.cover_image })).filter((c) => c.image), {
      limit: 8,
      variant: "md",
    });
  }, [featuredGrid, editorialProduct, lookbookTiles, categories]);

  return (
    <div data-testid="home-page">
      {/* CINEMATIC HERO */}
      <section
        className="relative w-full overflow-hidden md:overflow-visible border-b border-border bg-background"
        data-testid="hero-section"
      >
        <div className="max-w-[1480px] mx-auto px-0 md:px-8 lg:px-12">
          <div className="grid md:grid-cols-12 min-h-0 md:min-h-[720px]">
          {/* TEXT COLUMN — tablet/desktop (mobile uses overlay on image) */}
          <div className="hidden md:flex md:col-span-5 md:order-1 relative flex-col justify-center py-12 md:py-0 pr-6 xl:pr-10">
            <div className="relative min-h-[22rem] xl:min-h-[24rem]">
              <AnimatePresence initial={false}>
                <motion.div
                  key={`txt-${slide}`}
                  className="md:absolute md:inset-0 flex flex-col justify-center"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={HERO_TRANSITION}
                >
                <p className="overline mb-4" data-testid="hero-eyebrow">{current.eyebrow}</p>
                <h1
                  className="serif font-medium text-primary md:text-[3.35rem] xl:text-[3.75rem] leading-[0.98] tracking-[-0.02em]"
                  data-testid="hero-heading"
                >
                  <span className="block">{current.title[0]}</span>
                  <span className="block italic font-normal">{current.title[1]}</span>
                </h1>
                <p className="mt-5 text-primary/75 max-w-sm text-sm leading-relaxed">
                  {current.subtitle}
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link
                    to={current.cta.to}
                    data-testid="hero-cta-primary"
                    className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-7 sm:px-9 py-3.5 text-[11px] sm:text-xs uppercase tracking-[0.22em] hover:bg-primary/85 transition-colors group/cta"
                  >
                    {current.cta.label}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover/cta:translate-x-1" />
                  </Link>
                  <Link
                    to={current.cta2.to}
                    data-testid="hero-cta-secondary"
                    className="inline-flex items-center justify-center gap-2 border border-primary text-primary px-7 sm:px-9 py-3.5 text-[11px] sm:text-xs uppercase tracking-[0.22em] hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    {current.cta2.label}
                  </Link>
                </div>
              </motion.div>
              </AnimatePresence>
            </div>

            <div className="mt-8 md:absolute md:left-0 md:bottom-10 flex flex-wrap items-center gap-3 sm:gap-5">
              <span className="text-[10px] tracking-[0.32em] uppercase text-secondary">
                <span className="serif italic text-lg normal-case tracking-normal text-primary mr-2">
                  {String(slide + 1).padStart(2, "0")}
                </span>
                / {String(heroSlides.length).padStart(2, "0")}
              </span>
              <div className="flex gap-2">
                {heroSlides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goToSlide(i)}
                    data-testid={`hero-dot-${i}`}
                    aria-label={`Slide ${i + 1}`}
                    className="min-h-11 min-w-11 inline-flex items-center justify-center"
                  >
                    <span className={`block h-0.5 transition-all duration-500 ${i === slide ? "w-10 bg-primary" : "w-5 bg-primary/30"}`} />
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1.5 ml-2">
                <button onClick={prev} data-testid="hero-prev" aria-label="Previous" className="min-h-11 min-w-11 inline-flex items-center justify-center border border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground transition">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={next} data-testid="hero-next" aria-label="Next" className="min-h-11 min-w-11 inline-flex items-center justify-center border border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground transition">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* IMAGE COLUMN — editorial arch layout */}
          <div
            className="md:col-span-7 md:order-2 relative overflow-visible bg-transparent aspect-[4/5] max-h-[min(76vh,680px)] sm:max-h-[min(72vh,760px)] md:max-h-none md:aspect-auto md:min-h-[720px] md:pl-4 md:pr-0 md:py-8"
            onPointerEnter={() => setHeroPaused(true)}
            onPointerLeave={() => setHeroPaused(false)}
          >
            <HeroVisual slide={slide} slideData={current} />

            {/* Mobile overlay — headline + CTAs */}
            <div className="md:hidden absolute inset-0 z-0">
              <AnimatePresence initial={false}>
                <motion.div
                  key={`mob-img-${slide}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={HERO_TRANSITION}
                  className="absolute inset-0"
                >
                  <ProductImage
                    src={current.image}
                    alt="Oori Label hero"
                    priority
                    sizes={HERO_IMAGE_SIZES}
                    className="w-full h-full object-cover object-[center_20%]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/55 via-primary/10 to-transparent pointer-events-none" />
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="md:hidden absolute inset-x-0 bottom-0 z-10 px-4 pb-4 pt-16 text-primary-foreground">
              <div className="relative min-h-[9.5rem]">
              <AnimatePresence initial={false}>
                <motion.div
                  key={`mob-${slide}`}
                  className="absolute inset-x-0 bottom-0"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={HERO_TRANSITION}
                >
                  <p className="text-[10px] uppercase tracking-[0.28em] opacity-90">{current.eyebrow}</p>
                  <h1 className="serif text-[1.65rem] leading-[1.05] mt-2 font-medium">
                    {current.title[0]}{" "}
                    <span className="italic font-normal">{current.title[1]}</span>
                  </h1>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Link
                      to={current.cta.to}
                      className="inline-flex items-center justify-center gap-1.5 bg-background text-primary px-5 py-3 min-h-11 text-[10px] uppercase tracking-[0.2em]"
                    >
                      {current.cta.label}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                    <Link
                      to={current.cta2.to}
                      className="inline-flex items-center justify-center border border-background/70 text-background px-5 py-3 min-h-11 text-[10px] uppercase tracking-[0.2em]"
                    >
                      {current.cta2.label}
                    </Link>
                  </div>
                </motion.div>
              </AnimatePresence>
              </div>
              <div className="mt-4 flex items-center justify-between gap-3">
                <div className="flex gap-1.5">
                  {heroSlides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => goToSlide(i)}
                      aria-label={`Slide ${i + 1}`}
                      className="min-h-10 min-w-10 inline-flex items-center justify-center"
                    >
                      <span className={`block h-0.5 transition-all ${i === slide ? "w-8 bg-background" : "w-4 bg-background/40"}`} />
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={prev} aria-label="Previous" className="min-h-10 min-w-10 inline-flex items-center justify-center border border-background/50 text-background">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={next} aria-label="Next" className="min-h-10 min-w-10 inline-flex items-center justify-center border border-background/50 text-background">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Identity anchor */}
      <section className="border-b border-border bg-muted/30" data-testid="why-oori-section">
        <div className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-10">
          <p className="text-sm sm:text-base text-primary/75 leading-relaxed max-w-3xl mx-auto text-center">
            OORI is the Hindi word for home. Every piece we make is meant to feel exactly like that.
          </p>
        </div>
      </section>

      {/* CATEGORY GRID */}
      <section className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 mt-12 sm:mt-20 lg:mt-28" data-testid="categories-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline mb-3">Shop by Category</p>
            <h2 className="editorial-h2 max-w-xl">
              The <span className="italic">edits</span> that define this season.
            </h2>
          </div>
          <Link to="/category/dresses" className="hidden sm:inline-flex text-xs uppercase tracking-[0.22em] link-underline">
            View all
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {categories.map((c, i) => (
            <motion.div
              key={c.id}
              initial="hidden"
              animate="visible"
              variants={fade}
              custom={i}
            >
              <Link
                to={`/category/${c.slug}`}
                data-testid={`category-card-${c.slug}`}
                className="group block"
              >
                <div className="relative overflow-hidden aspect-[3/4] bg-muted">
                  <ProductImage
                    src={c.cover_image}
                    alt={c.name}
                    sizes="(max-width: 1024px) 50vw, 280px"
                    className="w-full h-full object-cover transition-transform ease-out duration-[1200ms] group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="overline text-background/85 mb-1">{c.count} pieces</p>
                    <h3 className="serif text-2xl lg:text-3xl text-background">{c.name}</h3>
                  </div>
                </div>
                <div className="hidden sm:flex items-center justify-between mt-3 text-xs text-secondary">
                  <span className="italic serif">{c.tagline}</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 mt-24 lg:mt-32" data-testid="featured-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline mb-3">Featured</p>
            <h2 className="editorial-h2">Bestsellers & new arrivals</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {featuredGrid.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      <RecentlyViewed className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 mt-24 lg:mt-32" />

      {/* EDITORIAL — founder / brand story */}
      <section className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 mt-24 lg:mt-32" data-testid="atelier-section">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          <div className="lg:col-span-7 relative aspect-[3/4] sm:aspect-[4/5] overflow-hidden bg-muted">
            <WhyOoriImage
              founderSrc={FOUNDER_IMAGE}
              productSrc={editorialProduct?.image}
              fallbackSrc={EDITORIAL_FALLBACK_IMAGE}
              alt={editorialProduct?.name || "OORI Label — crafted in India"}
            />
          </div>
          <div className="lg:col-span-5 flex flex-col justify-center py-4 lg:py-0">
            <p className="overline mb-4">Why OORI</p>
            <h3 className="editorial-h2">
              Clothes that feel like <span className="italic">home</span>.
            </h3>
            <p className="mt-6 text-primary/80 leading-relaxed max-w-md text-sm sm:text-base">
              OORI means home in Hindi. I started this label in Mumbai because I wanted
              pieces that felt mine — not borrowed, not traditional, but actually mine.
              Every design is made in small batches, slowly, with artisans we know by name.
            </p>
            <Link to="/about" className="btn-outline mt-8 self-start" data-testid="about-link">
              Our story
            </Link>
          </div>
        </div>
      </section>

      {/* LOOKBOOK */}
      <section className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 mt-24 lg:mt-32" data-testid="lookbook">
        <div className="text-center mb-12">
          <p className="overline mb-3">Lookbook · S/S 26</p>
          <h2 className="editorial-h2">In <span className="italic">conversation</span> with the sun.</h2>
        </div>
        {lookbookTiles.length >= 5 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 lg:grid-rows-2 gap-3 lg:gap-4">
            <Link
              to={`/product/${lookbookTiles[0].id}`}
              className="col-span-2 row-span-2 relative overflow-hidden bg-muted group aspect-[4/5] lg:aspect-auto lg:min-h-[480px]"
              data-testid="lookbook-0"
            >
              <ProductImage
                src={lookbookTiles[0].image}
                alt={lookbookTiles[0].name}
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary/70 via-primary/30 to-transparent pt-12 pb-3 px-3 sm:pb-4 sm:px-4">
                <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-background">
                  {lookbookTiles[0].name}
                </p>
              </div>
            </Link>
            {lookbookTiles.slice(1, 5).map((p, i) => (
              <Link
                to={`/product/${p.id}`}
                key={p.id}
                className="relative overflow-hidden bg-muted group aspect-square lg:aspect-auto lg:min-h-[232px]"
                data-testid={`lookbook-${i + 1}`}
              >
                <ProductImage
                  src={p.image}
                  alt={p.name}
                  sizes="(max-width: 1024px) 50vw, 25vw"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary/70 via-primary/30 to-transparent pt-10 pb-2.5 px-2.5 sm:pb-3 sm:px-3">
                  <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-background line-clamp-2">
                    {p.name}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* PROMISE STRIP */}
      <section className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 mt-24 lg:mt-32 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
        {[
          ["Shipping", freeShippingLabel],
          ["Eligible exchanges", "Report within 24 hrs with unboxing video"],
          ["Crafted in India", "By artisans we know by name"],
          ["Member benefits", "Early drops & private invites"],
        ].map(([t, s]) => (
          <div key={t}>
            <p className="serif text-lg">{t}</p>
            <p className="text-secondary mt-1.5 text-xs leading-relaxed">{s}</p>
          </div>
        ))}
      </section>
    </div>
  );
};

export default Home;
